import { useSetState, useUpdateEffect } from '@/shared/lib/customHooks';
import { preventAppScroll } from '@/shared/lib';
import _ from 'lodash';
import { useEffect, useImperativeHandle, useRef } from 'react';
import type { TableWrapperProps } from './tableWrapper.types';

/**
 * Drives a cursor-paginated RMA table: it owns the row collection, paging, ticket count and sort handling around the injected fetchData and fetchDataCount. Refetches from scratch whenever the serialized filter changes, tops up rows when tmpLimit is raised by paging or removal, releases the app scroll lock once loading ends, computes isEmptyData from isLoadedData with isEndOfData or isSearching, and exposes removeData, getData, addNewData, fetchCountAction, updateData and insertNewData through ref.
 *
 * @param tableOuterClasses - Extra class names passed through to the table shell.
 * @param pageProp - Initial page index, defaulting to 1.
 * @param sizePerPageProps - Initial page size, defaulting to 10.
 * @param fetchDataCount - Loader returning the total ticket count for a filter.
 * @param fetchData - Loader returning rows for a filter and limit.
 * @param filter - Query filter whose serialized value triggers a refetch.
 * @param isFetchCount - Whether the total count is fetched alongside the rows.
 * @param fetchErrorCallback - Callback invoked with any fetch error.
 * @param generateColumns - Column builder passed straight back to the caller.
 * @param getPagingCursor - Builder turning loaded rows into the paging cursor.
 * @param onSort - Callback receiving sortBy and sortOrder on a sort change.
 * @param handleRawData - Optional transform applied to freshly fetched rows.
 * @param fetchErrorMsg - Message logged next to a fetch error.
 * @param isSearching - Whether a search is active, used for the empty state.
 * @param restProps - Remaining table props forwarded untouched.
 * @param ref - Imperative handle receiving the row mutation helpers.
 * @returns The forwarded props, table state, column builder, change handler and empty-data flag.
 */
const useTableWrapper = (
  {
    tableOuterClasses,
    page: pageProp,
    sizePerPage: sizePerPageProps,
    fetchDataCount,
    fetchData,
    filter,
    isFetchCount,
    fetchErrorCallback,
    generateColumns,
    getPagingCursor,
    onSort,
    handleRawData,
    fetchErrorMsg,
    isSearching,
    ...restProps
  }: TableWrapperProps,
  ref: any,
) => {
  const initialPageSettings = {
    page: pageProp || 1,
    sizePerPage: sizePerPageProps || 10,
  };

  const lastQuery = useRef<any>(null);

  const [state, setState] = useSetState<any>({
    data: [],
    needToFetch: false,
    isLoadedData: true,
    ...initialPageSettings,
    tmpLimit: 0,
    isEndOfData: false,
    ticketCount: 0,
  });

  /**
   * Refreshes the total RMA ticket count shown by the table for the current filter. Stores the awaited fetchDataCount result as ticketCount, and on failure resets ticketCount to 0 and logs the error to the console.
   *
   * @returns Resolves once the count is stored or the failure is handled.
   */
  const fetchCountAction = async () => {
    try {
      const rmaCount = await fetchDataCount?.(filter);
      setState({ ticketCount: rmaCount });
    } catch (error) {
      setState({ ticketCount: 0 });
      console.error('Failed to fetch count: ', error);
    }
  };

  // TODO: Check condition to unshift data in specify table
  // const unshiftData = (data) => {
  //   // TODO: Unshift data
  // };

  /**
   * Removes the RMA rows whose key value appears in ids and repairs pagination afterwards. Returns early when nothing matched; otherwise it stores the reduced clone, recomputes page so it stays inside the shrunken row set, then queues a top-up fetch through tmpLimit with needToFetchCount when the paging cursor still holds a non-null value, or refreshes only the ticket count.
   *
   * @param ids - Identifier values of the rows to remove.
   * @param key - Row field compared against ids, defaulting to 'id'.
   * @returns Nothing.
   */
  const removeData = (ids: any, key = 'id') => {
    const setStateObject = {};

    const clonedData = _.cloneDeep(state.data);
    let hasRemoveData = false;
    _.remove(clonedData, (x) => {
      if (_.includes(ids, x?.[key])) {
        hasRemoveData = true;
        return true;
      }
      return false;
    });
    if (!hasRemoveData) {
      return;
    }
    _.assign(setStateObject, { data: clonedData });

    // *: Calculate new page after updating data
    let newPage = state.page;
    if (clonedData.length <= (state.page - 1) * state.sizePerPage) {
      newPage = Math.ceil(clonedData.length / state.sizePerPage);
    }
    if (newPage === 0) {
      newPage = 1;
    }
    _.assign(setStateObject, { page: newPage });

    const cursorObject = getPagingCursor?.(clonedData);
    /**
     * The paging-cursor keys that still hold a non-null value once the rows have been removed. Derived from the cursor object returned by getPagingCursor by filtering its keys with a lodash isNil check.
     *
     * @returns Cursor keys whose values are neither null nor undefined.
     */
    const removeNullCursor = _.filter(_.keys(cursorObject), (x) => !_.isNil(cursorObject[x]));
    const shouldFetchMore = removeNullCursor.length > 0;
    if (shouldFetchMore) {
      _.assign(setStateObject, {
        tmpLimit: state.data.length - clonedData.length,
        needToFetchCount: true,
      });
    } else if (isFetchCount) {
      fetchCountAction();
    }

    setState(setStateObject);
  };

  /**
   * Hands the table's currently loaded RMA rows to holders of the imperative handle. Returns a lodash deep clone of state.data so callers cannot mutate the stored rows.
   *
   * @returns A deep clone of the rows currently held in table state.
   */
  const getData = () => _.cloneDeep(state.data); // Prevent mutation data

  /**
   * Merges a partial object into the single RMA row matching the given id. Locates that row's index with lodash findIndex, deep-clones state.data, assigns the patch onto the cloned entry and stores the clone back as the table rows.
   *
   * @param id - Identifier of the row to patch.
   * @param object - Partial row fields assigned onto the matched row.
   * @returns Nothing.
   */
  const updateData = (id: any, object: any) => {
    /**
     * Position of the row whose id matches the requested id within the table's current rows. Derived from state.data through a lodash findIndex comparison on the id field.
     *
     * @returns Index of the matching row, or -1 when none matches.
     */
    const findIndex = _.findIndex(state.data, (d: any) => d.id === id);
    const cloneData = _.cloneDeep(state.data);
    _.assign(cloneData[findIndex], object);
    setState({ data: cloneData });
  };

  /**
   * Decides whether an RMA ticket identified by valueId belongs inside the range of rows the table already holds. Returns false when a loaded row already carries that id, true when state.isEndOfData is set or no rows are loaded, and otherwise checks with lodash whether any row's keyId value sits beyond valueId in the direction given by sortOrder.
   *
   * @param sortOrder - Sort direction of the loaded rows, either 'asc' or 'desc'.
   * @param keyId - Path of the row field holding the compared identifier.
   * @param valueId - Identifier value tested against the loaded range.
   * @returns True when the identifier falls inside the loaded range.
   */
  const handleCheckTicketInRangeList = (sortOrder: any, keyId: any, valueId: any) => {
    /**
     * Whether any row currently in table state already carries valueId at the keyId path. Derived from state.data with a lodash some lookup that reads the nested key through _.get.
     *
     * @returns True when a matching row is already loaded.
     */
    const isInList = _.some(state.data, (e) => valueId === _.get(e, keyId));

    if (isInList) {
      return false;
    }

    if (state.isEndOfData || _.isEmpty(state.data)) {
      return true;
    }

    if (sortOrder === 'asc') {
      return _.some(state.data, (e) => valueId < (_.get(e, keyId) || 0));
    }

    if (sortOrder === 'desc') {
      return _.some(state.data, (e) => valueId > (_.get(e, keyId) || 0));
    }

    return false;
  };

  // Insert list of new data into any position of current data

  // ----------------- NOTE: ONLY CORRECT FOR CASE ONLY ONE SORT FIELD IS TICKET ID -----------------
  /**
   * Inserts newly arrived RMA rows at their sorted position among the rows already loaded. Narrows ids to the one pinned by the last query when that key is filtered, keeps only ids inside the loaded range through the range check and refreshes just the count when none qualify, fetches each remaining id singly plus an optional count, then unions and re-orders the merged rows by keyId and the query sort order; failures are logged and forwarded to fetchErrorCallback.
   *
   * @param ids - Identifiers of the rows to insert, empty by default.
   * @param keyId - Row field used for matching and ordering, defaulting to 'id'.
   * @returns Resolves once the rows are inserted or the call is skipped.
   */
  const insertNewData = async (ids: any = [], keyId = 'id') => {
    try {
      // get and reset filter
      const filter = _.cloneDeep(lastQuery.current);
      delete filter.cursor;
      delete filter.limit;

      let listIdFilter = ids;

      if (filter?.[keyId]) {
        listIdFilter = _.filter(listIdFilter, (id) => id === filter[keyId]);

        if (_.isEmpty(listIdFilter)) {
          return;
        }
      }

      // Filter ids was included in range of the current data
      listIdFilter = _.filter(listIdFilter, (valueId) =>
        handleCheckTicketInRangeList(filter.sortOrder, keyId, valueId),
      );

      if (_.isEmpty(listIdFilter)) {
        if (isFetchCount) {
          fetchCountAction();
        }
        return;
      }

      const pending = [];

      _.forEach(listIdFilter, (id) => {
        const newFilter = {
          ...(filter || {}),
          [keyId]: id,
        };
        pending.push(fetchData?.(newFilter, 1));
      });

      if (isFetchCount) {
        pending.push(fetchDataCount?.(filter));
      }

      const fetchedData = await Promise.all(pending);
      const newTicketCount = isFetchCount ? fetchedData.pop() : state.ticketCount;

      if (_.isEmpty(fetchedData)) {
        return;
      }
      // Use setState by callback to fix error multi message in socket has been come at the same time;
      setState((prevState) => {
        const currentData = prevState.data;
        const newData = _.flatten(fetchedData);
        const mergeData = _.unionBy([...currentData, ...newData], 'id');
        const sortedData = _.orderBy(mergeData, keyId, filter.sortOrder);
        return { data: sortedData, ticketCount: newTicketCount };
      });
    } catch (error) {
      console.error('Failed to insert new data: ', error);
      if (_.isFunction(fetchErrorCallback)) {
        fetchErrorCallback(error);
      }
    }
  };

  /**
   * Fetches rows for ids pushed in from outside the table and merges them into the loaded RMA data. Drops ids already present, rebuilds the last query without its cursor, rmaIdCursor and limit keys, bails out under ascending sort before end-of-data, awaits the row fetch alongside an optional count fetch, then unions the results onto the previous rows by id, appending under ascending sort and prepending otherwise; errors reach fetchErrorCallback.
   *
   * @param ids - Identifiers of the rows to fetch and add.
   * @returns Resolves once the fetched rows are merged or the call is skipped.
   */
  const addNewData = async (ids: any) => {
    try {
      // Filter ids have been included in the current data
      /**
       * The subset of incoming ids that are not yet represented in the table's loaded rows. Derived by filtering ids against state.data with a lodash some check on each row's id.
       *
       * @returns Identifiers absent from the currently loaded rows.
       */
      const listIdFilter = _.filter(ids, (id) => !_.some(state.data, (d) => d.id === id));
      if (_.isEmpty(listIdFilter)) {
        return;
      }

      // get and reset filter
      const filter = _.cloneDeep(lastQuery.current);
      delete filter.cursor;
      delete filter?.rmaIdCursor;
      delete filter.limit;

      if (filter.sortOrder === 'asc') {
        if (!state.isEndOfData) {
          return;
        }
        _.assign(filter, getPagingCursor?.(state.data));
      }

      const promises = [];
      promises.push(fetchData?.(filter, listIdFilter.length));
      if (isFetchCount) {
        promises.push(fetchDataCount?.(filter));
      }

      const data = await Promise.all(promises);

      const fetchedData = data[0];
      const newTicketCount = isFetchCount ? data[1] : state.ticketCount;

      if (_.isEmpty(fetchedData)) {
        return;
      }

      // Use setState by callback to fix error multi message in socket has been come at the same time;
      setState((prevState) => {
        const currentData = prevState.data;
        let newData = [];
        if (filter.sortOrder === 'asc') {
          newData = _.unionBy([...currentData, ...fetchedData], 'id');
        } else {
          newData = _.unionBy([...fetchedData, ...currentData], 'id');
        }
        return { data: newData, ticketCount: newTicketCount };
      });
    } catch (error) {
      if (fetchErrorCallback && _.isFunction(fetchErrorCallback)) {
        fetchErrorCallback(error);
      }
    }
  };

  useImperativeHandle(ref, () => ({
    removeData,
    getData,
    addNewData,
    fetchCountAction,
    updateData,
    insertNewData,
  }));

  /**
   * Loads a page of RMA rows for the current filter and writes them into table state. Clears needToFetch, builds a cursor-augmented query when tmpLimit signals an append, awaits the row fetch plus an optional count fetch, ignores the response when a newer query was issued, runs handleRawData over the rows, unions appended rows by id, clamps page to the new maximum and sets isEndOfData; on error it empties the table and calls fetchErrorCallback.
   *
   * @returns Resolves once state reflects the fetched rows or the error branch.
   */
  const fetchDataAction = async () => {
    // prevent cannot fetch new data by new filter while fetching
    // related to useUpdateEffect
    setState({ needToFetch: false });

    const { data, ticketCount, tmpLimit, sizePerPage, needToFetchCount } = state;
    const filterQuery = {
      ...filter,
      ...(tmpLimit > 0 ? getPagingCursor?.(data) : {}),
    };
    const limit = tmpLimit > 0 ? tmpLimit : sizePerPage;

    lastQuery.current = {
      ...filterQuery,
      limit,
    };

    const fetchCollection = [fetchData?.(filterQuery, limit)];

    if (isFetchCount && (tmpLimit === 0 || needToFetchCount)) {
      fetchCollection.push(fetchDataCount?.(filter));
    }

    try {
      const currentQuery = {
        ...filterQuery,
        limit,
      };
      const fetch = await Promise.all(fetchCollection);

      // case: some previous API(s) return data longer than current one does
      // --> only handle the correct returned data
      if (JSON.stringify(currentQuery) === JSON.stringify(lastQuery.current)) {
        let rs = fetch && fetch.length > 0 ? fetch[0] : [];
        if (_.isFunction(handleRawData)) {
          rs = handleRawData(rs);
        }
        const tableData = tmpLimit > 0 ? _.unionBy([...data, ...rs], 'id') : rs;
        const rmaCount = fetch && fetch.length > 1 ? fetch[1] : ticketCount;

        // caculate new page;
        let { page } = state;
        const maxPage = Math.ceil(tableData.length / state.sizePerPage);
        if (page > maxPage) {
          page = Math.max(maxPage, 1);
        }

        setState({
          data: tableData,
          ticketCount: rmaCount,
          isLoadedData: true,
          page,
          // isEndOfData: tableData.length === rmaCount, // *: Old code
          isEndOfData: !rs.length || rs.length < limit, // *: New code
          tmpLimit: 0,
          needToFetchCount: false,
        });
      }
    } catch (error) {
      setState({
        data: [],
        ticketCount: 0,
        ...initialPageSettings,
        isLoadedData: true,
        isEndOfData: false,
        tmpLimit: 0,
        needToFetchCount: false,
      });
      if (fetchErrorCallback && _.isFunction(fetchErrorCallback)) {
        fetchErrorCallback(error);
      }
      console.error(fetchErrorMsg, error);
    }
  };

  /**
   * Applies a new page index and page size to the RMA table, requesting more rows when the target page reaches past what is loaded. Stores only page and sizePerPage once end-of-data is reached; otherwise it sets tmpLimit to the shortfall between sizePerPage times page and the loaded row count and marks the data as not loaded while that top-up is pending.
   *
   * @param sizePerPage - Number of rows shown per page.
   * @param page - Page index being switched to.
   * @returns Nothing.
   */
  const onPaginationChange = (sizePerPage: any, page: any) => {
    if (state.isEndOfData) {
      setState({
        page,
        sizePerPage,
      });
      return;
    }
    let tmpLimit = 0;
    if (sizePerPage * page > state.data.length) {
      tmpLimit = sizePerPage * page - state.data.length;
    }
    setState({
      isLoadedData: tmpLimit === 0,
      sizePerPage,
      page,
      tmpLimit,
    });
  };

  /**
   * Bridges the RMA table's change event to either pagination or sorting. Calls onPaginationChange and stops when the pagination current page or page size differs from state; otherwise it maps the sorter order to 'asc' or 'desc' and reports the field and direction through onSort.
   *
   * @param pagination - Pagination descriptor carrying current and pageSize.
   * @param filters - Column filter descriptor supplied by the table.
   * @param sorter - Sorter descriptor carrying the sorted field and order.
   * @returns Nothing.
   */
  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    const { current, pageSize } = pagination;
    const isChangePage = current && current !== state.page;
    const isChangePageSize = pageSize && pageSize !== state.sizePerPage;
    if (isChangePage || isChangePageSize) {
      onPaginationChange(pageSize, current);
      return;
    }

    const { field: sortBy, order } = sorter;
    const sortOrder = !order || order === 'ascend' ? 'asc' : 'desc';

    onSort?.({
      sortOrder,
      sortBy,
    });
  };

  useEffect(() => {
    setState({
      isLoadedData: false,
      needToFetch: true,
      ...initialPageSettings,
      tmpLimit: 0,
    });
  }, [JSON.stringify(filter)]);

  useUpdateEffect(() => {
    if (state.needToFetch || state.tmpLimit > 0) {
      fetchDataAction();
    }
  }, [state.needToFetch, state.tmpLimit]);

  useUpdateEffect(() => {
    if (state.isLoadedData) {
      preventAppScroll(false);
    }
  }, [state.isLoadedData]);

  const isEmptyData =
    state.isLoadedData && _.isEmpty(state.data) && (state.isEndOfData || isSearching);

  return {
    restProps,
    tableOuterClasses,
    state,
    generateColumns,
    handleTableChange,
    isEmptyData,
  };
};

export { useTableWrapper };
